import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Globe, Download, Percent, Share2, Copy, Check, AlertCircle } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { nanoid } from 'nanoid';
import { Toaster, toast } from 'react-hot-toast';
import ActorSearch from './components/ActorSearch';
import EstimateResults from './components/EstimateResults';
import TerritoryBreakdown from './components/TerritoryBreakdown';
import PDFDocument from './components/PDFDocument';
import { supabase, checkSupabaseConnection, saveEstimate } from './lib/supabase';

interface Actor {
  id: string;
  name: string;
  boxOfficePower: number;
  recentFilms: string[];
  profilePath?: string;
}

interface Territory {
  ask: number;
  take: number;
}

interface Territories {
  [region: string]: {
    [territory: string]: Territory;
  };
}

interface MarketTrend {
  trend: string;
  description: string;
}

const App = () => {
  const [selectedActors, setSelectedActors] = useState<Actor[]>([]);
  const [estimate, setEstimate] = useState<any>(null);
  const [title, setTitle] = useState('');
  const [director, setDirector] = useState('');
  const [globalAdjustment, setGlobalAdjustment] = useState(100);
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(null);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [territories, setTerritories] = useState<Territories>({
    europe: {
      'Benelux': { ask: 250000, take: 125000 },
      'France': { ask: 500000, take: 250000 },
      'Germany/Austria': { ask: 1000000, take: 500000 },
      'Greece': { ask: 50000, take: 25000 },
      'Italy': { ask: 500000, take: 250000 },
      'Portugal': { ask: 75000, take: 35000 },
      'Scandinavia': { ask: 250000, take: 175000 },
      'Spain': { ask: 350000, take: 175000 },
      'Turkey': { ask: 25000, take: 15000 },
      'UK/Ireland': { ask: 500000, take: 250000 }
    },
    easternEurope: {
      'Eastern Europe': { ask: 600000, take: 300000 },
      'Russia': { ask: 1000000, take: 500000 }
    },
    asia: {
      'China': { ask: 200000, take: 100000 },
      'Hong Kong': { ask: 100000, take: 50000 },
      'India': { ask: 75000, take: 35000 },
      'Indonesia': { ask: 50000, take: 25000 },
      'Japan': { ask: 100000, take: 50000 },
      'Malaysia': { ask: 50000, take: 25000 },
      'Philippines': { ask: 25000, take: 15000 },
      'Singapore': { ask: 25000, take: 12500 },
      'South Korea': { ask: 125000, take: 50000 },
      'Taiwan': { ask: 75000, take: 35000 },
      'Thailand': { ask: 25000, take: 15000 },
      'Vietnam': { ask: 15000, take: 10000 }
    },
    latinAmerica: {
      'All Territories': { ask: 1000000, take: 500000 }
    },
    australiaNz: {
      'Australia/New Zealand': { ask: 200000, take: 100000 }
    },
    northAmerica: {
      'Canada': { ask: 150000, take: 75000 },
      'United States': { ask: 2500000, take: 1250000 }
    },
    other: {
      'Airlines': { ask: 100000, take: 50000 },
      'Middle East': { ask: 350000, take: 175000 },
      'Israel': { ask: 15000, take: 10000 },
      'South Africa': { ask: 125000, take: 75000 }
    }
  });

  useEffect(() => {
    let mounted = true;
    let checkInterval: NodeJS.Timeout;

    const verifyConnection = async () => {
      if (!mounted) return;

      const isConnected = await checkSupabaseConnection();
      if (!mounted) return;
      
      setConnectionStatus(isConnected);
      
      if (isConnected === false) {
        toast.error('Database connection error. Please try again later.', {
          duration: 5000,
          id: 'supabase-connection-error'
        });
      }
    };
    
    verifyConnection();

    // Periodically check connection
    checkInterval = setInterval(verifyConnection, 30000);

    return () => {
      mounted = false;
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, []);

  const calculateMarketTrend = (actors: Actor[]): MarketTrend => {
    const avgPower = actors.reduce((sum, actor) => sum + actor.boxOfficePower, 0) / actors.length;
    
    if (avgPower >= 7) {
      return { 
        trend: "Strong Growth",
        description: "High market demand expected across all territories"
      };
    } else if (avgPower >= 5) {
      return {
        trend: "Moderate Growth",
        description: "Positive performance expected in key markets"
      };
    } else if (avgPower >= 3) {
      return {
        trend: "Stable",
        description: "Steady performance expected in established markets"
      };
    } else {
      return {
        trend: "Emerging",
        description: "Potential for growth in specific territories"
      };
    }
  };

  const calculateConfidence = (actors: Actor[]): number => {
    let confidence = 60;
    
    const avgPower = actors.reduce((sum, actor) => sum + actor.boxOfficePower, 0) / actors.length;
    confidence += Math.min(20, avgPower * 2.5);
    
    confidence += Math.min(10, (actors.length - 1) * 3);
    
    const avgRecentFilms = actors.reduce((sum, actor) => sum + actor.recentFilms.length, 0) / actors.length;
    confidence += Math.min(10, avgRecentFilms * 2);
    
    return Math.min(95, Math.round(confidence));
  };

  const generateEstimate = (actors: Actor[]) => {
    const totalPower = actors.reduce((sum, actor) => sum + actor.boxOfficePower, 0);
    const ensembleFactor = actors.length > 1 ? 1 + (actors.length - 1) * 0.25 : 1;
    const adjustedPower = totalPower * ensembleFactor;
    
    const adjustmentFactor = globalAdjustment / 100;
    const baseTotals = Object.values(territories).reduce((acc, region) => {
      const regionTotals = Object.values(region).reduce((regionAcc, territory) => ({
        ask: regionAcc.ask + territory.ask * adjustmentFactor,
        take: regionAcc.take + territory.take * adjustmentFactor
      }), { ask: 0, take: 0 });
      return {
        ask: acc.ask + regionTotals.ask,
        take: acc.take + regionTotals.take
      };
    }, { ask: 0, take: 0 });

    const powerFactor = adjustedPower / 8.5;
    const adjustedTerritories = Object.entries(territories).reduce((acc, [region, data]) => {
      acc[region] = Object.entries(data).reduce((terrAcc, [territory, values]) => {
        terrAcc[territory] = {
          ask: Math.round(values.ask * powerFactor * adjustmentFactor),
          take: Math.round(values.take * powerFactor * adjustmentFactor)
        };
        return terrAcc;
      }, {});
      return acc;
    }, {});

    const totalAdjustedAsks = baseTotals.ask * powerFactor;
    const marketTrend = calculateMarketTrend(actors);
    const confidence = calculateConfidence(actors);

    setEstimate({
      lowEstimate: Math.round(totalAdjustedAsks * 0.8),
      highEstimate: Math.round(totalAdjustedAsks * 1.2),
      confidence,
      marketTrend: marketTrend.trend,
      marketTrendDescription: marketTrend.description,
      territories: adjustedTerritories
    });
  };

  const handleActorSelect = (actor: Actor) => {
    const newActors = [...selectedActors, actor];
    setSelectedActors(newActors);
    generateEstimate(newActors);
  };

  const handleActorRemove = (actorId: string) => {
    const newActors = selectedActors.filter(actor => actor.id !== actorId);
    setSelectedActors(newActors);
    if (newActors.length > 0) {
      generateEstimate(newActors);
    } else {
      setEstimate(null);
    }
  };

  const handleTerritoryValueChange = (region: string, territory: string, field: 'ask' | 'take', value: number) => {
    setTerritories(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [territory]: {
          ...prev[region][territory],
          [field]: value
        }
      }
    }));
    if (selectedActors.length > 0) {
      generateEstimate(selectedActors);
    }
  };

  const handleGlobalAdjustment = (value: number) => {
    setGlobalAdjustment(value);
    if (selectedActors.length > 0) {
      generateEstimate(selectedActors);
    }
  };

  const handleSaveAndShare = async () => {
    if (!estimate || !title) {
      toast.error('Please add a title and generate an estimate first');
      return;
    }

    if (!connectionStatus) {
      toast.error('Database connection error. Please try again later.');
      return;
    }

    const savingToast = toast.loading('Saving estimate...');

    try {
      const estimateId = nanoid(10);
      const estimateData = {
        id: estimateId,
        title,
        director,
        actors: selectedActors,
        territories,
        estimate_data: estimate
      };

      const { data, error } = await saveEstimate(estimateData);

      if (error) throw error;
      if (!data) throw new Error('Failed to save estimate');

      const shareableUrl = `/estimate/${estimateId}`;
      const fullUrl = window.location.origin + shareableUrl;
      
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 3000);
      } catch (clipboardError) {
        console.warn('Could not copy to clipboard:', clipboardError);
      }
      
      toast.dismiss(savingToast);
      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Estimate saved successfully!
                </p>
                <div className="mt-1 text-sm text-gray-500">
                  <p className="mb-2">Share this link to view the estimate:</p>
                  <div className="flex items-center space-x-2 bg-gray-100 p-2 rounded">
                    <input
                      type="text"
                      value={fullUrl}
                      readOnly
                      className="flex-1 bg-transparent border-none text-sm focus:outline-none"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(fullUrl);
                          setCopiedToClipboard(true);
                          setTimeout(() => setCopiedToClipboard(false), 3000);
                          toast.success('Link copied to clipboard!');
                        } catch (err) {
                          toast.error('Please copy the link manually');
                        }
                      }}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      {copiedToClipboard ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      ), {
        duration: 10000,
      });
    } catch (error: any) {
      toast.dismiss(savingToast);
      console.error('Error saving estimate:', error);
      toast.error(`Failed to save estimate: ${error.message || 'Please try again'}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-yellow-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p className="text-sm font-medium">
            Beta Release: This tool is currently in development. All estimates are preliminary and should be verified with industry professionals.
          </p>
        </div>
      </div>

      {connectionStatus === false && (
        <div className="bg-red-500 text-white">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm font-medium">
              Database connection error. Please try again later.
            </p>
          </div>
        </div>
      )}

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 5000,
            style: {
              background: '#059669',
              color: '#fff',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#DC2626',
              color: '#fff',
            },
          },
        }}
      />

      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://c32e022225eda3f5b7ba82cc03e373d6.cdn.bubble.io/f1739203314831x826979416146093000/Cinelaunch%20-%20Digital%20Rocket.png?_gl=1*qgthuz*_gcl_au*MTAyNzEzNzAzOS4xNzM1NDI3MTkz*_ga*NDc0MDQxMTk5LjE3Mjc3OTY1NjY.*_ga_BFPVR2DEE2*MTczOTIwMzI4NC44My4xLjE3MzkyMDMzMDIuNDIuMC4w" 
                alt="CineLaunch Logo" 
                className="h-10 w-auto"
              />
              <h1 className="text-2xl font-bold text-gray-900">Film Value Estimator</h1>
            </div>
            <div className="flex items-center space-x-4">
              {estimate && (
                <>
                  <button
                    onClick={handleSaveAndShare}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Save & Share
                  </button>
                  <PDFDownloadLink
                    document={
                      <PDFDocument
                        title={title}
                        director={director}
                        territories={estimate.territories}
                      />
                    }
                    fileName={`${title || 'estimate'}-sales-estimate.pdf`}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    {({ loading }) => (
                      <>
                        <Download className="h-5 w-5 mr-2" />
                        {loading ? 'Generating PDF...' : 'Export PDF'}
                      </>
                    )}
                  </PDFDownloadLink>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4">Project Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter film title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Director
                  </label>
                  <input
                    type="text"
                    value={director}
                    onChange={(e) => setDirector(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Enter director name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cast (up to 4 actors)
                  </label>
                  <ActorSearch 
                    onSelect={handleActorSelect}
                    selectedActors={selectedActors}
                    onRemove={handleActorRemove}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Percent className="h-4 w-4 mr-1" />
                    Global Value Adjustment
                  </label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="range"
                      min="50"
                      max="150"
                      value={globalAdjustment}
                      onChange={(e) => handleGlobalAdjustment(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm font-medium w-16">{globalAdjustment}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-8 space-y-6">
            {estimate ? (
              <>
                <EstimateResults estimate={estimate} />
                <TerritoryBreakdown 
                  title={title}
                  director={director}
                  actor={selectedActors[0]}
                  territories={estimate.territories}
                  baseValues={territories}
                  onValueChange={handleTerritoryValueChange}
                />
              </>
            ) : (
              <div className="bg-white p-8 rounded-lg shadow-md text-center">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select actors to generate estimates
                </h3>
                <p className="text-gray-600">
                  Estimates are based on historical box office data and current market trends
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default App;